/**
 * Truncates a string to the specified length and adds ellipsis if truncated
 * @param text - The string to truncate
 * @param maxLength - Maximum length of the string (default: 20)
 * @param addEllipsis - Whether to add "..." at the end if truncated (default: true)
 * @returns Truncated string
 */
export const truncateString = (
  text: string,
  maxLength: number = 20,
  addEllipsis: boolean = true
): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  if (text.length <= maxLength) {
    return text;
  }
  
  const truncated = text.substring(0, maxLength);
  return addEllipsis ? `${truncated}...` : truncated;
};

/**
 * Truncates a string to the specified length without ellipsis
 * @param text - The string to truncate
 * @param maxLength - Maximum length of the string
 * @returns Truncated string without ellipsis
 */
export const truncateStringNoEllipsis = (
  text: string,
  maxLength: number
): string => {
  return truncateString(text, maxLength, false);
};

/**
 * Formats a file name for display by truncating with ellipsis before the last characters
 * Display up to maxLength characters total. The file extension is preserved separately,
 * and preserveEndChars applies only to the base filename (before the extension).
 * 
 * @param fileName - The file name to format
 * @param maxLength - Maximum total length (default: 60)
 * @param preserveEndChars - Number of characters to preserve at the end of the base filename (default: 10)
 * @returns Formatted file name
 * 
 * @example
 * formatFileName("Emmanuel_Cruz_MandatoryReportingTraining_LoremIpsumDolors.pdf")
 * // Returns: "Emmanuel_Cruz_MandatoryReportingTrainin...umDolors.pdf"
 * 
 * @example
 * // Use with useFileNameLength hook for responsive behavior:
 * const { maxLength, preserveEndChars } = useFileNameLength();
 * formatFileName(fileName, maxLength, preserveEndChars);
 */
export const formatFileName = (
  fileName: string,
  maxLength: number = 60,
  preserveEndChars: number = 10
): string => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }

  if (fileName.length <= maxLength) {
    return fileName;
  }

  // Extract file extension (e.g., ".pdf", ".docx")
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < fileName.length - 1;
  
  let baseName = fileName;
  let extension = '';
  
  if (hasExtension) {
    baseName = fileName.substring(0, lastDotIndex);
    extension = fileName.substring(lastDotIndex); // includes the dot
  }

  const ellipsis = '...';
  const availableLength = maxLength - extension.length - ellipsis.length;
  
  // If even with truncation we can't fit, just truncate the whole thing
  if (availableLength <= preserveEndChars) {
    const truncatedLength = maxLength - ellipsis.length;
    return fileName.substring(0, truncatedLength) + ellipsis;
  }

  const startLength = availableLength - preserveEndChars;
  const start = baseName.substring(0, startLength);
  const end = baseName.substring(baseName.length - preserveEndChars);
  
  return `${start}${ellipsis}${end}${extension}`;
};

/**
 * Extracts suburb name from an address string
 * Handles multiple formats like:
 * - "45 George St, The Rocks Sydney, NSW 2000, Australia"
 * - "45 George St Sydney NSW 2000"
 * - "112 Arthur St, Alexandra QLD 4740, Australia"
 * - "123 Main Street Melbourne Victoria 3000"
 * - "45 George St, Sydney NSW\t2000"
 * - "10 Commonwealth Street, Surry Hills NSW, Australia"
 * Returns: "The Rocks Sydney", "Sydney", "Alexandra", "Melbourne", "Sydney", "Surry Hills"
 */
export const extractSuburbFromAddress = (address: string): string => {
  if (!address) return "";

  const cleanAddress = address.trim();
  
  // Normalize whitespace: replace tabs, newlines, and multiple spaces with single space
  let processedAddress = cleanAddress
    .replace(/\s+/g, ' ') // Replace any whitespace (including tabs, newlines) with single space
    .replace(/,?\s*(Australia|USA|UK|Canada)\s*$/i, '') // Remove country names
    .trim();

  // Split by comma
  const commaParts = processedAddress.split(',').map(part => part.trim());

  if (commaParts.length === 0) return "";

  // If address has commas (e.g., "45 George St, The Rocks Sydney, NSW 2000")
  if (commaParts.length >= 2) {
    // Find the part that contains state code and postcode (e.g., "NSW 2000")
    const statePostcodeIndex = commaParts.findIndex(part => 
      /^[A-Z]{2,3}\s+\d{4,}$/.test(part.trim())
    );

    if (statePostcodeIndex > 0) {
      // Suburb is everything between street address and state/postcode
      const suburbParts = commaParts.slice(1, statePostcodeIndex);
      if (suburbParts.length > 0) {
        return suburbParts.join(', ').trim();
      }
    } else {
      // Handle case where last part is "Suburb State Postcode"
      // e.g., "Blacktown Supermarket, Main Street, Blacktown NSW 2148"
      const lastPart = commaParts[commaParts.length - 1];
      const suburbWithPostcodeMatch = lastPart.match(/^([A-Za-z\s]+?)\s+[A-Z]{2,3}\s+\d{4,}$/);
      if (suburbWithPostcodeMatch && suburbWithPostcodeMatch[1]) {
        return suburbWithPostcodeMatch[1].trim();
      }
      
      // Handle case where last part is "Suburb State" without postcode
      // e.g., "10 Commonwealth Street, Surry Hills NSW"
      const suburbWithStateMatch = lastPart.match(/^([A-Za-z\s]+?)\s+[A-Z]{2,3}$/);
      if (suburbWithStateMatch && suburbWithStateMatch[1]) {
        return suburbWithStateMatch[1].trim();
      }
    }
  }

  // If no commas or state/postcode not found, try space-separated format
  // Pattern: "Street Name Suburb State Postcode"
  // Match: capture everything after street number/name and before state code
  const spaceSeparatedMatch = processedAddress.match(
    /^[\d\w\s]+?\s+([A-Za-z\s]+?)\s+([A-Z]{2,3})\s+\d{4,}$/
  );
  
  if (spaceSeparatedMatch && spaceSeparatedMatch[1]) {
    return spaceSeparatedMatch[1].trim();
  }

  return "";
};

/**
 * Capitalizes a name: if the name is entirely lowercase or entirely uppercase,
 * returns it with the first letter uppercased and the rest lowercased.
 * Mixed-case names are returned as-is.
 * @param name - The name string to capitalize
 * @returns Properly capitalized name
 */
export const capitalizeName = (name: string): string => {
  if (!name) return name;
  if (name === name.toLowerCase() || name === name.toUpperCase()) {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }
  return name;
};